// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The rule engine (M2-07, M2-09, M2-10, M2-12).
//
// A 1990s ruleset needs stacked timed modes, ball locks, multiball, jackpot ladders, combos,
// a bonus ladder and a wizard mode — and every one of them has to be authorable by a player
// who never gets to execute a line of code. This is the interpreter that makes that trade.
//
// **Three properties are load-bearing, and all three are about reproducibility.**
//
//  1. **Rules fire in declaration order.** Not in the order events arrived, not in hash
//     order. A rulesheet is read top to bottom by its author, and that is the order it runs.
//  2. **Raised events drain breadth-first, to a depth of 8.** A rule whose action changes a
//     variable that triggers another rule does not recurse into it mid-action; the new
//     trigger joins the next wave. Depth-first would make a rule's effect depend on where in
//     its action list the trigger happened to sit.
//  3. **Time is a step count.** `durationMs` is converted once at load. Nothing here reads a
//     clock, so a replay validated on a server at a different frame rate expires the same
//     timer on the same step (PH-086).
//
// The engine is pure in the sense that matters: it reads switches and produces score, lamp
// states and a list of **effects**. It never touches the simulation directly. A rule cannot
// read the ball's position, the network, the clock or another player's state — it closes
// switches and fires coils, exactly as a real machine's CPU does (SEC-012).

import { STEP_HZ } from '../sim/constants'
import { roundHalfAwayFromZero } from '../math/deterministic'
import { evaluate, evaluateCondition } from './expression'
import type { EvalContext } from './expression'
import { VarStore } from './VarStore'
import { resolveSettings, settingNumber } from '../machine/settings'
import type { ResolvedSettings } from '../machine/settings'
import { OPERATOR_BALL_SAVE } from '../machine/types'
import type {
  Action,
  Expression,
  MachineDefinition,
  MachineMode,
  MachineRule,
} from '../machine/types'

/** 04 §11.5. Static analysis proves a rulesheet cannot reach this; the guard is a backstop. */
export const MAX_EVENT_DEPTH = 8

// Trigger kinds, as numbers so the C# port is a transliteration and the hot loop compares
// integers rather than parsing strings.
export const T_SWITCH = 0
export const T_VAR = 1
export const T_MODE_START = 2
export const T_MODE_END = 3
export const T_TIMER_EXPIRE = 4
export const T_BALL_START = 5
export const T_BALL_END = 6

/**
 * What a `coil` action asked the driver board to do.
 *
 * A solenoid driver can pulse a coil, hold it on, or leave it off, and the difference is not
 * cosmetic. A kickback that is *armed* catches the ball; one that is not lets it drain. With
 * only "pulse" available, an unlit outlane would have to capture the ball and wait to be told
 * what to do with it, which is a trap by another name.
 */
export const COIL_PULSE = 0
export const COIL_ENABLE = 1
export const COIL_DISABLE = 2

export interface Trigger {
  readonly kind: number
  readonly name: string
}

/** Something the machine must physically do. The host consumes these; the engine never acts. */
export interface RuleEffect {
  readonly kind: 'coil' | 'sound' | 'speech' | 'display' | 'knocker' | 'multiball' | 'lockBall' | 'ballSave' | 'extraBall'
  readonly id: string
  readonly value: number
  readonly step: number
}

interface TimerState {
  readonly id: string
  readonly durationSteps: number
  readonly pausesDuringMultiball: boolean
  remaining: number
  running: boolean
}

interface ModeState {
  readonly spec: MachineMode
  active: boolean
  /** Shots hit this run, as a bitmask over `spec.shots`. */
  shotsHit: number
  startedStep: number
}

interface CompiledRule {
  readonly rule: MachineRule
  readonly trigger: Trigger
  /** Declaration index. Firing order is by this, always. */
  readonly order: number
}

export function msToSteps(ms: number): number {
  if (ms < 0) return -1
  return roundHalfAwayFromZero(ms * (STEP_HZ / 1000))
}

/** Parse a rule's `on` string once, at construction. */
export function parseTrigger(on: string): Trigger | undefined {
  const parts = on.split(':')
  const head = parts[0]
  const name = parts[1] ?? ''
  if (head === 'switch') return { kind: T_SWITCH, name }
  if (head === 'var') return { kind: T_VAR, name }
  if (head === 'timer') return parts[2] === 'expire' ? { kind: T_TIMER_EXPIRE, name } : undefined
  if (head === 'mode') {
    if (parts[2] === 'start') return { kind: T_MODE_START, name }
    if (parts[2] === 'end') return { kind: T_MODE_END, name }
    return undefined
  }
  if (head === 'ball') {
    if (name === 'start') return { kind: T_BALL_START, name: '' }
    if (name === 'end') return { kind: T_BALL_END, name: '' }
  }
  return undefined
}

function key(kind: number, name: string): string {
  return `${kind}\u0000${name}`
}

export interface RuleEngineOptions {
  readonly playerCount?: number
  /** Balls per game. Read from `meta` when omitted. */
  readonly ballsPerGame?: number
}

export class RuleEngine {
  readonly vars: VarStore
  readonly definition: MachineDefinition

  /**
   * The machine's operator settings at their declared defaults (M9-35).
   *
   * Read for exactly one thing today: `enableBallSave: "operator"`. Resolved once in the
   * constructor rather than per action, because it is a pure read of a document that cannot
   * change while a game is running — and because a rule firing on every switch closure is the
   * wrong place to walk a settings block.
   */
  private readonly settings: ResolvedSettings

  /** Score per player, index 0-based. Exact: binary64 holds 2^53, far past any pinball score. */
  readonly scores: number[]
  currentPlayer = 0
  currentBall = 1

  /** Simulation step index the host last handed in. The engine's only notion of time. */
  step = 0

  /** Effects raised since the last drain. */
  private readonly effects: RuleEffect[] = []

  private readonly timers: TimerState[] = []
  private readonly timerIndex = new Map<string, number>()
  private readonly modes: ModeState[] = []
  private readonly modeIndex = new Map<string, number>()
  private readonly lamps = new Map<string, number>()
  private readonly lampGroups = new Map<string, string[]>()

  private readonly rules: CompiledRule[] = []
  private readonly byTrigger = new Map<string, number[]>()

  /** Switches closed during the current step. Cleared at the end of each step. */
  private readonly closedNow = new Set<string>()

  private readonly pending: Trigger[] = []
  private readonly bonusMultiplierVar: string | undefined

  /** Balls currently in play, set by the host. Drives `ballsInPlay` and multiball pauses. */
  ballsInPlay = 1

  /** Diagnostics: how often the depth guard stopped a cascade. Should stay zero. */
  depthCutoffs = 0

  /**
   * How many times each declared rule has fired, by declaration index.
   *
   * Not a debug aid bolted on afterwards: it is the only way to answer "can this rule ever
   * fire?", which is the question a ninety-rule sheet cannot be read to answer. A rule whose
   * count stays zero across a full scripted game is either unreachable or wrongly guarded,
   * and both are silent failures without this. One integer increment per firing.
   */
  readonly fired: number[] = []

  /**
   * Everything the engine carries between steps (M4-28a).
   *
   * Not the rules, the timers' durations or the mode specifications — those come from the
   * definition and a snapshot restores into an engine built from the same one. What is here is
   * the part that would otherwise be lost: how much of each timer is left, which modes are
   * running and how far through, every lamp's current code, and every variable.
   *
   * `closedNow` and `pending` are deliberately absent. Both are cleared within a step, so at a
   * step boundary — the only place a snapshot is ever taken — they are empty by construction, and
   * carrying an always-empty collection is an invitation to start relying on it.
   */
  snapshot(): RuleEngineState {
    return {
      scores: [...this.scores],
      currentPlayer: this.currentPlayer,
      currentBall: this.currentBall,
      step: this.step,
      ballsInPlay: this.ballsInPlay,
      depthCutoffs: this.depthCutoffs,
      fired: [...this.fired],
      timers: this.timers.map((t) => ({ remaining: t.remaining, running: t.running })),
      modes: this.modes.map((m) => ({
        active: m.active,
        shotsHit: m.shotsHit,
        startedStep: m.startedStep,
      })),
      lamps: [...this.lamps.entries()].map(([id, code]) => [id, code] as const),
      vars: this.vars.snapshot(),
    }
  }

  restore(state: RuleEngineState): void {
    if (state.timers.length !== this.timers.length || state.modes.length !== this.modes.length) {
      throw new Error('RuleEngine.restore: the snapshot came from a different definition')
    }
    for (const [i, score] of state.scores.entries()) this.scores[i] = score
    this.currentPlayer = state.currentPlayer
    this.currentBall = state.currentBall
    this.step = state.step
    this.ballsInPlay = state.ballsInPlay
    this.depthCutoffs = state.depthCutoffs
    for (const [i, count] of state.fired.entries()) this.fired[i] = count
    for (const [i, t] of state.timers.entries()) {
      this.timers[i]!.remaining = t.remaining
      this.timers[i]!.running = t.running
    }
    for (const [i, m] of state.modes.entries()) {
      this.modes[i]!.active = m.active
      this.modes[i]!.shotsHit = m.shotsHit
      this.modes[i]!.startedStep = m.startedStep
    }
    // Cleared and rewritten rather than merged: a lamp that was on when the snapshot was taken
    // and is absent from it was off, and merging would leave it lit for ever.
    this.lamps.clear()
    for (const [id, code] of state.lamps) this.lamps.set(id, code)
    this.vars.restore(state.vars)
    this.closedNow.clear()
    this.pending.length = 0
    this.effects.length = 0
  }

  private readonly evalContext: EvalContext

  constructor(definition: MachineDefinition, options: RuleEngineOptions = {}) {
    this.definition = definition
    this.settings = resolveSettings(definition)
    const playerCount = options.playerCount ?? definition.meta.playersMax
    this.scores = new Array<number>(playerCount).fill(0)

    const rules = definition.rules
    this.vars = new VarStore(rules?.vars ?? [], playerCount)

    for (const t of rules?.timers ?? []) {
      this.timerIndex.set(t.id, this.timers.length)
      this.timers.push({
        id: t.id,
        durationSteps: msToSteps(t.durationMs),
        pausesDuringMultiball: t.pausesDuringMultiball === true,
        remaining: 0,
        running: false,
      })
    }

    for (const m of rules?.modes ?? []) {
      this.modeIndex.set(m.id, this.modes.length)
      this.modes.push({ spec: m, active: false, shotsHit: 0, startedStep: -1 })
    }

    for (const lamp of definition.lamps ?? []) {
      this.lamps.set(lamp.id, 0)
      if (lamp.group !== undefined) {
        const list = this.lampGroups.get(lamp.group)
        if (list) list.push(lamp.id)
        else this.lampGroups.set(lamp.group, [lamp.id])
      }
    }

    const declared = rules?.rules ?? []
    for (let i = 0; i < declared.length; i++) {
      const trigger = parseTrigger(declared[i]!.on)
      if (trigger === undefined) continue
      const compiled: CompiledRule = { rule: declared[i]!, trigger, order: i }
      const k = key(trigger.kind, trigger.name)
      const list = this.byTrigger.get(k)
      // Appended in declaration order, and only ever read by ascending index.
      if (list) list.push(this.rules.length)
      else this.byTrigger.set(k, [this.rules.length])
      this.rules.push(compiled)
      this.fired.push(0)
    }

    this.bonusMultiplierVar = rules?.scoring?.bonus?.multiplier?.var

    this.evalContext = {
      varValue: (id) => this.vars.get(id, this.currentPlayer),
      setCount: (id) => this.vars.countOf(id, this.currentPlayer),
      setHas: (id, member) => this.vars.hasMember(id, this.currentPlayer, member),
      modeActive: (id) => this.isModeActive(id),
      timerRunning: (id) => {
        const i = this.timerIndex.get(id)
        return i !== undefined && this.timers[i]!.running
      },
      ballsInPlay: () => this.ballsInPlay,
      playerBall: () => this.currentBall,
      switchClosed: (id) => this.closedNow.has(id),
      lampState: (id) => this.lamps.get(id) ?? 0,
    }
  }

  // --- host interface ----------------------------------------------------------

  get score(): number {
    return this.scores[this.currentPlayer] ?? 0
  }

  isModeActive(id: string): boolean {
    const i = this.modeIndex.get(id)
    return i !== undefined && this.modes[i]!.active
  }

  lampOf(id: string): number {
    return this.lamps.get(id) ?? 0
  }

  timerRemaining(id: string): number {
    const i = this.timerIndex.get(id)
    return i === undefined ? 0 : this.timers[i]!.remaining
  }

  /** Ids of every rule that has never fired. Empty is the property a rulesheet wants. */
  neverFired(): string[] {
    const out: string[] = []
    for (let i = 0; i < this.rules.length; i++) {
      if (this.fired[i] === 0) out.push(this.rules[i]!.rule.id)
    }
    return out
  }

  drainEffects(): RuleEffect[] {
    const out = this.effects.slice()
    this.effects.length = 0
    return out
  }

  /** Close a switch. Queued rather than acted on, so a step resolves as one wave. */
  closeSwitch(id: string): void {
    this.closedNow.add(id)
    this.pending.push({ kind: T_SWITCH, name: id })
  }

  startBall(player: number, ball: number): void {
    this.currentPlayer = player
    this.currentBall = ball
    this.vars.resetBall(player)
    this.vars.clearDirty()
    for (const mode of this.modes) {
      mode.active = false
      mode.shotsHit = 0
    }
    for (const timer of this.timers) {
      timer.running = false
      timer.remaining = 0
    }
    this.pending.push({ kind: T_BALL_START, name: '' })
  }

  endBall(): void {
    this.pending.push({ kind: T_BALL_END, name: '' })
  }

  /**
   * Start a mode as if a rule had asked for it (M9-22, 11 §4).
   *
   * *"Force state — Jump to any mode, multiball or wizard state to test its rules."*
   *
   * **Through `startMode` rather than by setting `active`.** A mode is not a flag: starting one
   * resets its shot count, starts its timer, runs its `onStart` hook and raises the trigger that
   * every rule listening for `mode:x:start` fires on. A test tool that set the flag would put the
   * machine in a state no game can ever be in, and the author would be testing rules against it.
   *
   * **The machine's own stacking rule is obeyed**, which is why this answers rather than returns
   * nothing: a non-stackable mode refuses to start on top of another, and an author who was not
   * told would think the button was broken. The refusal is the machine being right.
   *
   * The gate is the caller's: `MachineRuntime` opens this only for the Builder, because a scored
   * game whose player could start the wizard mode is not a leaderboard.
   */
  forceMode(id: string): boolean {
    const i = this.modeIndex.get(id)
    if (i === undefined) return false
    this.startMode(id, this.pending)
    return this.modes[i]!.active
  }

  /** End a mode as if it had been ended by a rule. Answers whether it was running. */
  forceEndMode(id: string): boolean {
    const i = this.modeIndex.get(id)
    if (i === undefined) return false
    const mode = this.modes[i]!
    if (!mode.active) return false
    this.endMode(mode, 'ended')
    return true
  }

  /** Which modes are running, in declaration order. */
  activeModes(): readonly string[] {
    return this.modes.filter((mode) => mode.active).map((mode) => mode.spec.id)
  }

  /**
   * The mode a display should name, and how much of its clock is left.
   *
   * **The score scene has drawn this since M2 and nothing ever supplied it.** `DmdScoreView.mode`
   * is read by `scoreMain` on both clients — the label and the draining bar are already there,
   * pixel for pixel identical — and `scoreView` simply never set it. So a player has never been
   * told which mode is running or how long they have, on any machine, on either client.
   *
   * **The first running mode in declaration order**, which is the order `activeModes` reports and
   * the order the author wrote. A panel has room for one name; picking the one closest to expiring
   * would arguably serve the player better and would make the label jump between two stacked modes
   * as their clocks crossed, which on a 5 x 7 dot panel reads as a glitch rather than as
   * information. The author's order is a deliberate statement and this defers to it.
   *
   * **`remaining` is 0 for a mode with no timer**, and that is not "expired" — it is "no clock".
   * The bar's width is `round(remaining x width)`, so zero draws nothing and the label stands on
   * its own, which is exactly what an untimed mode should look like. A full bar that never moved
   * would be a timer that appears stuck.
   */
  runningMode(): RunningMode | undefined {
    for (const mode of this.modes) {
      if (!mode.active) continue
      return { id: mode.spec.id, title: mode.spec.title, remaining: this.remainingFraction(mode) }
    }
    return undefined
  }

  /** How much of a mode's named timer is left, 0..1. Zero when it has none. */
  private remainingFraction(mode: ModeState): number {
    if (mode.spec.timer === undefined) return 0
    const i = this.timerIndex.get(mode.spec.timer)
    if (i === undefined) return 0
    const timer = this.timers[i]!
    // A duration of zero is a machine definition that would divide by it. Answered as no clock
    // rather than as NaN, which would reach a raster width and become a loop bound.
    if (timer.durationSteps <= 0) return 0
    const fraction = timer.remaining / timer.durationSteps
    return fraction < 0 ? 0 : fraction > 1 ? 1 : fraction
  }

  /**
   * Advance one simulation step: tick timers, then resolve every queued trigger.
   *
   * Called once per simulation step by the host, with the step index. The engine never
   * derives time any other way.
   */
  advance(stepIndex: number): void {
    this.step = stepIndex
    this.tickTimers()
    this.resolve()
    this.closedNow.clear()
  }

  private tickTimers(): void {
    // By index, so two runtimes expire two timers due on the same step in the same order.
    for (let i = 0; i < this.timers.length; i++) {
      const t = this.timers[i]!
      if (!t.running) continue
      if (t.pausesDuringMultiball && this.ballsInPlay > 1) continue
      t.remaining--
      if (t.remaining > 0) continue
      t.running = false
      t.remaining = 0
      this.pending.push({ kind: T_TIMER_EXPIRE, name: t.id })
      // A mode whose clock has run out times out, before any rule sees the expiry.
      for (let m = 0; m < this.modes.length; m++) {
        const mode = this.modes[m]!
        if (mode.active && mode.spec.timer === t.id) this.endMode(mode, 'timeout')
      }
    }
  }

  /** Drain the trigger queue breadth-first. */
  private resolve(): void {
    // A variable changed from outside rule processing — by the host, or by a mode hook that
    // ran during `tickTimers` — must raise its trigger too. Draining only inside the loop
    // meant an external write raised nothing at all, because the loop never started.
    const wave0 = this.pending.splice(0, this.pending.length)
    const changed0: string[] = []
    this.vars.drainChanged(changed0)
    for (const id of changed0) wave0.push({ kind: T_VAR, name: id })

    let wave = wave0
    let depth = 0

    while (wave.length > 0) {
      if (depth >= MAX_EVENT_DEPTH) {
        // Unreachable if the load-time analysis did its job. Counted, never thrown: a
        // runaway rulesheet should cost a player a cascade, not their game.
        this.depthCutoffs++
        this.pending.length = 0
        return
      }
      const raised: Trigger[] = []

      for (const trigger of wave) {
        this.fireModeHooks(trigger, raised)
        const matches = this.byTrigger.get(key(trigger.kind, trigger.name))
        if (matches === undefined) continue
        for (let i = 0; i < matches.length; i++) {
          const compiled = this.rules[matches[i]!]!
          if (!evaluateCondition(compiled.rule.when, this.evalContext)) continue
          this.fired[matches[i]!]!++
          this.runActions(compiled.rule.do, raised)
        }
      }

      // Anything raised during this wave, plus whatever the pending queue collected, becomes
      // the next wave. Variable changes are folded in here so a `var:` trigger is one level
      // deeper than the action that caused it, never re-entrant within it.
      const changed: string[] = []
      this.vars.drainChanged(changed)
      for (const id of changed) raised.push({ kind: T_VAR, name: id })
      if (this.pending.length > 0) {
        raised.push(...this.pending.splice(0, this.pending.length))
      }

      wave = raised
      depth++
    }
  }

  /** A mode's own hooks: a shot in its set, and its start/end. */
  private fireModeHooks(trigger: Trigger, raised: Trigger[]): void {
    if (trigger.kind !== T_SWITCH) return
    for (let i = 0; i < this.modes.length; i++) {
      const mode = this.modes[i]!
      if (!mode.active) continue
      const shots = mode.spec.shots
      if (shots === undefined) continue
      const at = shots.indexOf(trigger.name)
      if (at < 0) continue
      const bit = 2 ** at
      if (Math.floor(mode.shotsHit / bit) % 2 === 1) continue
      mode.shotsHit += bit
      if (mode.spec.onShot) this.runActions(mode.spec.onShot, raised)
      if (mode.shotsHit === 2 ** shots.length - 1) {
        if (mode.spec.onComplete) this.runActions(mode.spec.onComplete, raised)
        this.endMode(mode, 'completed')
      }
    }
  }

  // --- actions (M2-09) -----------------------------------------------------------

  private runActions(actions: readonly Action[], raised: Trigger[]): void {
    for (const action of actions) this.runAction(action, raised)
  }

  private runAction(action: Action, raised: Trigger[]): void {
    // Keys are read by name, never enumerated, so a definition's key order cannot change
    // what an action does.
    if ('award' in action) {
      // A literal, or an expression — which is what makes a building jackpot ladder
      // authorable at all: `{ "award": { "var": "jackpotValue" } }`. It runs in the same
      // bounded evaluator as a `when`, so it carries the same guarantees (SEC-012).
      const base = this.amountOf(action['award'])
      const multiplied =
        action['multiplyByBonusX'] === true && this.bonusMultiplierVar !== undefined
          ? base * Math.max(1, this.vars.get(this.bonusMultiplierVar, this.currentPlayer))
          : base
      this.scores[this.currentPlayer] = (this.scores[this.currentPlayer] ?? 0) + multiplied
      return
    }
    if ('set' in action) {
      const spec = action['set']
      if (!isObject(spec) || typeof spec['var'] !== 'string') return
      const id = spec['var']
      const to = spec['to']
      if (to === 'empty') this.vars.set(id, this.currentPlayer, 0)
      else if (typeof to === 'boolean') this.vars.set(id, this.currentPlayer, to ? 1 : 0)
      else this.vars.set(id, this.currentPlayer, this.amountOf(to))
      return
    }
    if ('add' in action) {
      const spec = action['add']
      if (!isObject(spec) || typeof spec['var'] !== 'string') return
      const id = spec['var']
      if (typeof spec['member'] === 'string') this.vars.addMember(id, this.currentPlayer, spec['member'])
      else if (spec['amount'] !== undefined) this.vars.add(id, this.currentPlayer, this.amountOf(spec['amount']))
      return
    }
    if ('lamp' in action) {
      const spec = action['lamp']
      if (isObject(spec) && typeof spec['id'] === 'string') {
        this.lamps.set(spec['id'], lampCode(spec['state']))
      }
      return
    }
    if ('lampGroup' in action) {
      const spec = action['lampGroup']
      if (isObject(spec) && typeof spec['id'] === 'string') {
        const code = lampCode(spec['state'])
        for (const id of this.lampGroups.get(spec['id']) ?? []) this.lamps.set(id, code)
      }
      return
    }
    if ('coil' in action) {
      const spec = action['coil']
      if (isObject(spec) && typeof spec['id'] === 'string') {
        this.effect('coil', spec['id'], coilCode(spec['state']))
      }
      return
    }
    if ('startTimer' in action) {
      const id = action['startTimer']
      if (typeof id !== 'string') return
      const i = this.timerIndex.get(id)
      if (i === undefined) return
      const t = this.timers[i]!
      t.remaining = t.durationSteps
      t.running = true
      return
    }
    if ('stopTimer' in action) {
      const id = action['stopTimer']
      if (typeof id !== 'string') return
      const i = this.timerIndex.get(id)
      if (i === undefined) return
      this.timers[i]!.running = false
      this.timers[i]!.remaining = 0
      return
    }
    if ('startMode' in action) {
      const id = action['startMode']
      if (typeof id === 'string') this.startMode(id, raised)
      return
    }
    if ('endMode' in action) {
      const spec = action['endMode']
      const id = typeof spec === 'string' ? spec : isObject(spec) && typeof spec['id'] === 'string' ? spec['id'] : undefined
      if (id === undefined) return
      const i = this.modeIndex.get(id)
      if (i !== undefined && this.modes[i]!.active) this.endMode(this.modes[i]!, 'ended')
      return
    }
    if ('startMultiball' in action) {
      const spec = action['startMultiball']
      const balls = isObject(spec) && typeof spec['balls'] === 'number' ? spec['balls'] : 3
      this.effect('multiball', '', balls)
      if (isObject(spec) && typeof spec['ballSaveMs'] === 'number') {
        this.effect('ballSave', spec['saveAllBalls'] === true ? 'allBalls' : '', msToSteps(spec['ballSaveMs']))
      }
      return
    }
    if ('lockBall' in action) {
      const spec = action['lockBall']
      const device = isObject(spec) && typeof spec['device'] === 'string' ? spec['device'] : ''
      this.effect('lockBall', device, typeof spec === 'object' && spec !== null && (spec as Record<string, unknown>)['virtual'] === true ? 1 : 0)
      return
    }
    if ('enableBallSave' in action) {
      const ms = action['enableBallSave']
      // **`"operator"` is the whole of M9-35.** 04 §15 declares one `ballSaveSeconds` and a
      // rulesheet grants several different saves — NEON NOVA grants five: 12 s at ball start,
      // 20 s at each of two multiballs, 30 s at another and 60 s in wizard mode. M9-34 tried to
      // make the operator's number govern all of them and flattened four; the C# hundred-game
      // replay family caught it and it was taken back out.
      //
      // So the author says *which* grant the coin door governs, and the rest stay literal. Nothing
      // is inferred: a rule that names no setting is exactly the rule it was before this existed,
      // which is why every fixture recorded against the old vocabulary still replays.
      //
      // Resolved here rather than in `Game`, because the effect's value has always meant *steps*
      // and a sentinel in a numeric field is a second meaning for one number.
      if (ms === OPERATOR_BALL_SAVE) {
        this.effect('ballSave', '', msToSteps(settingNumber(this.settings, 'ballSaveSeconds', 0) * 1000))
        return
      }
      this.effect('ballSave', '', typeof ms === 'number' ? msToSteps(ms) : 0)
      return
    }
    if ('display' in action) {
      const spec = action['display']
      if (isObject(spec) && typeof spec['scene'] === 'string') this.effect('display', spec['scene'], 0)
      return
    }
    if ('sound' in action) {
      const spec = action['sound']
      if (isObject(spec) && typeof spec['cue'] === 'string') this.effect('sound', spec['cue'], 0)
      return
    }
    if ('speech' in action) {
      const spec = action['speech']
      if (isObject(spec) && typeof spec['cue'] === 'string') this.effect('speech', spec['cue'], 0)
      return
    }
    if ('knocker' in action) {
      this.effect('knocker', '', 0)
      return
    }
    if ('awardExtraBall' in action) {
      this.effect('extraBall', '', 1)
    }
  }

  /**
   * A number an action asked for: a literal, or an expression evaluated here and now.
   *
   * Non-finite results collapse to zero rather than poisoning a score. A NaN reaching
   * `scores[]` would make every later comparison false and the leaderboard row unorderable,
   * and it would do it silently.
   */
  private amountOf(value: unknown): number {
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0
    if (typeof value === 'boolean') return value ? 1 : 0
    if (!isObject(value)) return 0
    const n = evaluate(value as Expression, this.evalContext)
    return Number.isFinite(n) ? n : 0
  }

  private effect(kind: RuleEffect['kind'], id: string, value: number): void {
    this.effects.push({ kind, id, value, step: this.step })
  }

  // --- modes (M2-12) --------------------------------------------------------------

  private startMode(id: string, raised: Trigger[]): void {
    const i = this.modeIndex.get(id)
    if (i === undefined) return
    const mode = this.modes[i]!
    if (mode.active) return
    // A non-stackable mode refuses to start on top of anything. This is the standard 1990s
    // behaviour and it is a design decision, not a limitation: unrestricted stacking makes
    // every mode's scoring depend on which others happen to be running.
    if (mode.spec.stackable !== true) {
      for (const other of this.modes) if (other.active) return
    }
    mode.active = true
    mode.shotsHit = 0
    mode.startedStep = this.step
    if (mode.spec.timer !== undefined) {
      const t = this.timerIndex.get(mode.spec.timer)
      if (t !== undefined) {
        this.timers[t]!.remaining = this.timers[t]!.durationSteps
        this.timers[t]!.running = true
      }
    }
    if (mode.spec.onStart) this.runActions(mode.spec.onStart, raised)
    raised.push({ kind: T_MODE_START, name: id })
  }

  private endMode(mode: ModeState, reason: 'completed' | 'timeout' | 'ended'): void {
    if (!mode.active) return
    mode.active = false
    if (reason === 'timeout' && mode.spec.onTimeout) {
      this.runActions(mode.spec.onTimeout, this.pending)
    }
    if (mode.spec.timer !== undefined) {
      const t = this.timerIndex.get(mode.spec.timer)
      if (t !== undefined) {
        this.timers[t]!.running = false
        this.timers[t]!.remaining = 0
      }
    }
    this.pending.push({ kind: T_MODE_END, name: mode.spec.id })
  }

  /** Bit-exact fingerprint of rule state, for golden transcripts and replay checkpoints. */
  fingerprint(): number[] {
    const out: number[] = [this.currentPlayer, this.currentBall, ...this.scores, ...this.vars.snapshot()]
    for (const t of this.timers) out.push(t.running ? 1 : 0, t.remaining)
    for (const m of this.modes) out.push(m.active ? 1 : 0, m.shotsHit)
    // Lamps by declaration order, not map order.
    for (const lamp of this.definition.lamps ?? []) out.push(this.lamps.get(lamp.id) ?? 0)
    return out
  }
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

const LAMP_CODES: Record<string, number> = {
  off: 0,
  on: 1,
  slow: 2,
  fast: 3,
  pulse: 4,
  strobe: 5,
}

/** `pulse` is the default, because a coil with no state named is a coil being fired. */
function coilCode(state: unknown): number {
  if (state === 'enable') return COIL_ENABLE
  if (state === 'disable') return COIL_DISABLE
  return COIL_PULSE
}

/** Lamp states are codes, so a blink pattern is step-driven and therefore replayable. */
function lampCode(state: unknown): number {
  if (typeof state !== 'string') return 0
  const known = LAMP_CODES[state]
  if (known !== undefined) return known
  // `chase:<group>` and any other parameterised pattern collapse to a single code here; the
  // renderer resolves the parameter. The engine only needs it to be a number it can compare.
  return state.startsWith('chase') ? 6 : 1
}

/** The rule engine's mutable state, for a chunk boundary (M4-28a). */
export interface RuleEngineState {
  readonly scores: readonly number[]
  readonly currentPlayer: number
  readonly currentBall: number
  readonly step: number
  readonly ballsInPlay: number
  readonly depthCutoffs: number
  readonly fired: readonly number[]
  /** In declaration order, so a snapshot and an engine line up without matching by name. */
  readonly timers: readonly { readonly remaining: number; readonly running: boolean }[]
  readonly modes: readonly {
    readonly active: boolean
    readonly shotsHit: number
    readonly startedStep: number
  }[]
  /** Lamp id to code. A map rather than an array, because lamps are set as they are first used. */
  readonly lamps: readonly (readonly [string, number])[]
  readonly vars: readonly number[]
}

/** What a display needs to know about the mode a player is in the middle of. */
export interface RunningMode {
  readonly id: string
  /** The author's own words. What goes on the glass. */
  readonly title: string
  /** How much of its timer is left, 0..1. Zero means it has no timer, not that it has expired. */
  readonly remaining: number
}
